import {
  AuthenticationDetails,
  CognitoRefreshToken,
  CognitoUser,
  CognitoUserPool,
  CognitoUserSession,
  ICognitoStorage,
} from 'amazon-cognito-identity-js';

import { getSignedInUsername, setSignedInUsername } from './tokenStore';

const userPoolId = process.env.REACT_APP_COGNITO_USER_POOL_ID || '';
const appClientId = process.env.REACT_APP_COGNITO_APP_CLIENT_ID || '';

if (!userPoolId || !appClientId) {
  console.error(
    'Cognito environment variables are not set! Please create a .env file in apps/admin-panel.'
  );
}

/**
 * amazon-cognito-identity-js defaults to window.localStorage, which means it
 * writes the id, access AND refresh tokens there under
 * `CognitoIdentityServiceProvider.*` keys — independently of anything this app
 * does. Keeping our own access token out of localStorage would be pointless
 * while the SDK persists its copy beside it, so the SDK gets an in-memory store.
 *
 * Consequence: nothing Cognito holds survives a reload, so session restore goes
 * through refreshSession() below using the one value we do persist.
 */
class InMemoryCognitoStorage implements ICognitoStorage {
  private readonly items = new Map<string, string>();

  setItem(key: string, value: string): void {
    this.items.set(key, value);
  }

  getItem(key: string): string | null {
    return this.items.get(key) ?? null;
  }

  removeItem(key: string): void {
    this.items.delete(key);
  }

  clear(): void {
    this.items.clear();
  }
}

const cognitoStorage = new InMemoryCognitoStorage();

const userPool =
  userPoolId && appClientId
    ? new CognitoUserPool({
        UserPoolId: userPoolId,
        ClientId: appClientId,
        Storage: cognitoStorage,
      })
    : null;

const requireUserPool = (): CognitoUserPool => {
  if (!userPool) {
    throw new Error(
      'Missing Cognito config. Set REACT_APP_COGNITO_USER_POOL_ID and REACT_APP_COGNITO_APP_CLIENT_ID in apps/admin-panel/.env'
    );
  }
  return userPool;
};

export interface AuthTokens {
  idToken: string;
  accessToken: string;
  refreshToken: string;
}

export interface NewPasswordRequiredChallenge {
  challenge: 'NEW_PASSWORD_REQUIRED';
  cognitoUser: CognitoUser;
  userAttributes: Record<string, string>;
}

export type SignInResult = AuthTokens | NewPasswordRequiredChallenge;

export const isNewPasswordChallenge = (
  result: SignInResult
): result is NewPasswordRequiredChallenge =>
  (result as NewPasswordRequiredChallenge).challenge === 'NEW_PASSWORD_REQUIRED';

const sessionToTokens = (session: CognitoUserSession): AuthTokens => ({
  idToken: session.getIdToken().getJwtToken(),
  accessToken: session.getAccessToken().getJwtToken(),
  refreshToken: session.getRefreshToken().getToken(),
});

export const AuthService = {
  signIn: (email: string, password: string): Promise<SignInResult> => {
    return new Promise((resolve, reject) => {
      const pool = requireUserPool();

      const authenticationDetails = new AuthenticationDetails({
        Username: email,
        Password: password,
      });

      const cognitoUser = new CognitoUser({
        Username: email,
        Pool: pool,
        Storage: cognitoStorage,
      });

      cognitoUser.authenticateUser(authenticationDetails, {
        onSuccess: (session: CognitoUserSession) => {
          // Not a credential — just the username needed to rebuild a
          // CognitoUser on the next page load so refreshSession can run.
          setSignedInUsername(email);
          resolve(sessionToTokens(session));
        },
        onFailure: (err) => {
          reject(err);
        },
        newPasswordRequired: (userAttributes: Record<string, string>) => {
          // email and email_verified are read-only on the user and cannot be
          // sent back when completing the challenge.
          delete userAttributes.email_verified;
          delete userAttributes.email;
          resolve({
            challenge: 'NEW_PASSWORD_REQUIRED',
            cognitoUser,
            userAttributes,
          });
        },
      });
    });
  },

  completeNewPasswordChallenge: (
    cognitoUser: CognitoUser,
    newPassword: string,
    userAttributes: Record<string, string>
  ): Promise<AuthTokens> => {
    return new Promise((resolve, reject) => {
      cognitoUser.completeNewPasswordChallenge(newPassword, userAttributes, {
        onSuccess: (session: CognitoUserSession) => {
          resolve(sessionToTokens(session));
        },
        onFailure: (err) => {
          reject(err);
        },
      });
    });
  },

  /**
   * Exchanges a stored refresh token for a fresh session on page load.
   *
   * The SDK's own cache is in memory (see InMemoryCognitoStorage), so
   * `getCurrentUser()` returns null after a reload and the CognitoUser has to be
   * rebuilt from the persisted username.
   */
  refreshSession: (refreshToken: string): Promise<AuthTokens> => {
    return new Promise((resolve, reject) => {
      const pool = requireUserPool();
      const username = getSignedInUsername();

      if (!username) {
        reject(new Error('No signed-in user to refresh.'));
        return;
      }

      const cognitoUser = new CognitoUser({
        Username: username,
        Pool: pool,
        Storage: cognitoStorage,
      });

      cognitoUser.refreshSession(
        new CognitoRefreshToken({ RefreshToken: refreshToken }),
        (err, session: CognitoUserSession) => {
          if (err || !session) {
            reject(err ?? new Error('Refresh returned no session.'));
            return;
          }
          resolve(sessionToTokens(session));
        }
      );
    });
  },

  signOut: () => {
    setSignedInUsername(null);
    cognitoStorage.clear();
    if (!userPool) {
      return;
    }
    const cognitoUser = userPool.getCurrentUser();
    if (cognitoUser) {
      cognitoUser.signOut();
    }
  },

  getCurrentUser: (): CognitoUser | null => {
    if (!userPool) {
      return null;
    }
    return userPool.getCurrentUser();
  },
};
